import type {
  MembershipAssignmentStatus,
  PackAssignmentStatus,
} from '@goelectric/shared-types';
import {
  darkColors,
  neutralColors,
  semanticColors,
} from '@goelectric/ui-components';
import type { StatusIconKey } from '@goelectric/ui-components';
import { StatusBadgeShell, StatusIcon, useStatusColor } from '@/components/StatusBadgeShell';
import {
  MEMBERSHIP_ASSIGNMENT_STATUS_DESCRIPTIONS,
  MEMBERSHIP_ASSIGNMENT_STATUS_LABELS,
  PACK_ASSIGNMENT_STATUS_DESCRIPTIONS,
  PACK_ASSIGNMENT_STATUS_LABELS,
} from '../utils/format';

interface StatusMeta {
  color: string;
  colorDark: string;
  iconKey: StatusIconKey;
}

/** Color e icono por estado: el texto siempre acompaña al color (WCAG 2.2 AA). */
const PACK_STATUS_META: Record<PackAssignmentStatus, StatusMeta> = {
  ACTIVE: { color: semanticColors.success, colorDark: darkColors.success, iconKey: 'check' },
  EXHAUSTED: { color: semanticColors.warning, colorDark: darkColors.warning, iconKey: 'warning' },
  EXPIRED: { color: semanticColors.warning, colorDark: darkColors.warning, iconKey: 'warning' },
  CANCELLED: {
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

const MEMBERSHIP_STATUS_META: Record<MembershipAssignmentStatus, StatusMeta> = {
  ACTIVE: { color: semanticColors.success, colorDark: darkColors.success, iconKey: 'check' },
  EXPIRED: { color: semanticColors.warning, colorDark: darkColors.warning, iconKey: 'warning' },
  CANCELLED: {
    color: neutralColors.textMuted,
    colorDark: darkColors.textMuted,
    iconKey: 'offline',
  },
};

/** Estado de una asignación de pack con color + icono + texto. */
export function PackAssignmentStatusBadge({
  status,
  forceDark = false,
}: {
  status: PackAssignmentStatus;
  forceDark?: boolean;
}) {
  const meta = PACK_STATUS_META[status];
  const label = PACK_ASSIGNMENT_STATUS_LABELS[status];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={label}
      title={`${label}: ${PACK_ASSIGNMENT_STATUS_DESCRIPTIONS[status]}`}
    />
  );
}

/** Estado de una asignación de membresía con color + icono + texto. */
export function MembershipAssignmentStatusBadge({
  status,
  forceDark = false,
}: {
  status: MembershipAssignmentStatus;
  forceDark?: boolean;
}) {
  const meta = MEMBERSHIP_STATUS_META[status];
  const label = MEMBERSHIP_ASSIGNMENT_STATUS_LABELS[status];
  const color = useStatusColor(meta.color, meta.colorDark, forceDark);
  return (
    <StatusBadgeShell
      color={color}
      icon={<StatusIcon iconKey={meta.iconKey} />}
      label={label}
      title={`${label}: ${MEMBERSHIP_ASSIGNMENT_STATUS_DESCRIPTIONS[status]}`}
    />
  );
}
